import { useFormContext, useWatch } from 'react-hook-form'
import { VoicePicker, SelectedVoiceCard } from '../index'
import { usePlayableGreeting } from '../../hooks/usePlayableGreeting'
import type { Voice } from '@/types/assistant'

/**
 * Voices Tab - Inline voice picker with the current selection in detail
 *
 * Includes:
 * - Inline voice picker (filters, recommended/all view, audio previews)
 * - Detail card for the currently selected voice, above the picker
 *
 * Language settings live on the Personality & languages tab.
 */
export function VoicesTab() {
  const { control, setValue } = useFormContext()

  // Watch form values
  const voiceId = useWatch({ control, name: 'voice_id' }) as number | undefined
  const defaultLanguage = useWatch({ control, name: 'default_language' }) as string
  const additionalLanguages = useWatch({ control, name: 'additional_languages' }) as string[]
  const assistantId = useWatch({ control, name: 'id' }) as number

  // What previews should speak: the resolved dynamic greeting when dynamic
  // mode is on, the static greeting field otherwise.
  const playableGreeting = usePlayableGreeting()

  // Handle voice selection
  const handleVoiceSelect = (voice: Voice) => {
    setValue('voice_id', voice.id, { shouldDirty: true })
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Currently selected voice, in detail */}
      {voiceId != null && (
        <SelectedVoiceCard
          voiceId={voiceId}
          assistantId={assistantId}
          defaultLanguage={defaultLanguage}
          additionalLanguages={additionalLanguages}
          greeting={playableGreeting}
        />
      )}

      {/* Voice Picker */}
      <VoicePicker
        selectedVoiceId={voiceId}
        onSelect={handleVoiceSelect}
        greeting={playableGreeting}
        defaultLanguage={defaultLanguage}
        additionalLanguages={additionalLanguages}
        assistantId={assistantId}
      />
    </div>
  )
}

export default VoicesTab
